// Role of this file:
// Listen for an action
// produce a side effect which is communicate with the server via http,
// bring data back and dispatch a new action (success or fail)

import { Inject, Injectable } from "@angular/core";

// Actions is an observable that listens to the types of actions
// being dispatched and we can respond accordingly
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { SnackBarService } from "#core/services";
import * as LoginStore from "#login/store";
import { LocalStorage } from "#services/local-storage.service";
import { USER_SERVICE } from "@carnegie-learning/data-access-angular-fido";
import { CoursesService, ProfileService } from "@carnegie-learning/data-access-angular-passport";
import { UserService } from "@carnegie-learning/data-access-fido";
import { Store } from "@ngrx/store";
import { Course } from "cli-types";
import * as coursesActions from "../actions/courses.action";

@Injectable()
export class CoursesEffects {
    constructor(
        private actions$: Actions,
        private coursesService: CoursesService,
        private localStorageService: LocalStorage,
        @Inject(USER_SERVICE) private userService: UserService,
        private profileService: ProfileService,
        private loginStore: Store<LoginStore.UserState>,
        private snackBarService: SnackBarService
    ) {}

    // this is where we listen for the action type
    loadCourses$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.loadStudentCourses))
            // produce a side effect
            .pipe(
                switchMap(() => {
                    return this.coursesService.getStudentCourses().pipe(
                        // Each teacher-class primaryTeacher attempts to get fido Profile
                        switchMap((courses) => {
                            const courseList = courses.data;
                            if (!courseList.length) {
                                // here is when the studnet not in any teacher-classes and we should display a warning msessage
                                // using snackbar working so it won't have a duration
                                this.snackBarService.working(
                                    "This account is not enrolled in any classes yet, please ask your teacher for help",
                                    "snackbar-error"
                                );
                            }
                            const teacherIds: string[] = [];
                            courseList.forEach((course) => {
                                const tchId = course.primaryTeacher?.userGUID;
                                if (tchId && !teacherIds.includes(tchId)) {
                                    teacherIds.push(tchId);
                                }
                            });
                            return this.userService.getUsers(teacherIds).pipe(
                                map((users) => {
                                    const updatedCourseList: Course[] = [];
                                    courseList.forEach((c) => {
                                        const foundTeacher = users.find(
                                            (user) =>
                                                user.id ===
                                                c.primaryTeacher?.userGUID
                                        );
                                        if (foundTeacher) {
                                            c.primaryTeacher = {
                                                ...c.primaryTeacher,
                                                firstName:
                                                    foundTeacher?.givenName,
                                                lastName: foundTeacher?.surName,
                                                eAddress: foundTeacher?.email,
                                            };
                                        }
                                        updatedCourseList.push(c);
                                    });
                                    return updatedCourseList;
                                })
                            );
                        }),
                        // mapping over all the teacher-classes and returning an action
                        map((courseList) =>
                            coursesActions.loadStudentCoursesSuccess({
                                payload: courseList,
                            })
                        ),
                        // need to wrap the error in 'of' so it becomes an observable action
                        catchError((error) =>
                            of(coursesActions.loadStudentCoursesFail(error))
                        )
                    );
                })
            )
    );

    // this is where we listen for the action type
    loadCourse$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.loadCourse))
            // produce a side effect
            .pipe(
                map((action) => action.payload),
                switchMap((courseID) => {
                    return this.coursesService.getCourse(courseID).pipe(
                        // mapping over all the teacher-classes and returning an action
                        map((courseData) =>
                            coursesActions.loadCourseSuccess({
                                payload: courseData,
                            })
                        ),
                        // need to wrap the error in 'of' so it becomes an observable action
                        catchError((error) =>
                            of(coursesActions.loadCourseFail(error))
                        )
                    );
                })
            )
    );

    setSelectCourse$ = createEffect(() =>
        this.actions$.pipe(ofType(coursesActions.setSelectedCourse)).pipe(
            map((action) => action.payload),
            switchMap((course) => {
                const userData = this.localStorageService.get("userProfile");

                let courseID: number | null = null;
                if (course) {
                    courseID = course.courseID;
                }

                if (courseID === userData.lastCourse) {
                    // No changes! Skip the API hit and go straight to the routing.
                    return of(
                        coursesActions.updateSelectedCourseSuccess({ courseID })
                    );
                }

                const payload = { lastCourse: courseID };
                return this.profileService.setUserSelectedCourse(payload).pipe(
                    map(() => {
                        return coursesActions.updateSelectedCourseSuccess({
                            courseID,
                        });
                    }),
                    catchError((error) =>
                        of(coursesActions.updateSelectedCourseFail())
                    )
                );
            })
        )
    );

    updateSelectedCourseSuccess$ = createEffect(() =>
        this.actions$
            .pipe(ofType(coursesActions.updateSelectedCourseSuccess))
            .pipe(
                map(({ courseID }) => {
                    this.loginStore.dispatch(
                        LoginStore.setLastCourse({ courseID })
                    );
                    const userData =
                        this.localStorageService.get("userProfile");
                    userData.lastCourse = courseID;
                    this.localStorageService.set("userProfile", userData);
                    return coursesActions.selectedCourseNoAction();
                })
            )
    );
}
